import { useLayoutEffect, useRef } from "react";

// Shared across route mounts, bounded to the most recent 100 history entries.
const historyPositions = new Map<string, { x: number; y: number }>();

// Pass a namespaced history key when an app embeds a second router.
export function usePageScrollRestoration({ entryKey, pathname, historyNavigation, delegated = false }: {
  entryKey: string; pathname: string; historyNavigation: boolean; delegated?: boolean;
}) {
  const previous = useRef<{ pathname: string; delegated: boolean } | null>(null);
  const activeKey = useRef(entryKey);
  const restoring = useRef(false);

  useLayoutEffect(() => {
    const original = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const save = () => {
      if (!restoring.current) historyPositions.set(activeKey.current, { x: window.scrollX, y: window.scrollY });
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => { window.removeEventListener("scroll", save); window.history.scrollRestoration = original; };
  }, []);

  useLayoutEffect(() => {
    const prior = previous.current;
    previous.current = { pathname, delegated };
    const currentPosition = historyPositions.get(activeKey.current) ?? { x: window.scrollX, y: window.scrollY };
    activeKey.current = entryKey;
    const saved = historyNavigation ? historyPositions.get(entryKey) : undefined;
    const target = saved ?? (prior?.pathname === pathname ? currentPosition : { x: 0, y: 0 });
    historyPositions.set(entryKey, target);
    if (historyPositions.size > 100) historyPositions.delete(historyPositions.keys().next().value!);
    // Filters and consumed deep links must not interrupt an in-page smooth scroll.
    if (prior?.pathname === pathname && !historyNavigation) return;
    // A child router handles moves within its section. The parent still records
    // positions for entering/leaving that section.
    if (prior?.delegated && delegated) return;
    restoring.current = true;
    let observer: ResizeObserver | undefined;
    let timer: number | undefined;
    let frame: number | undefined;
    let stopped = false;
    const stop = () => {
      stopped = true;
      restoring.current = false;
      observer?.disconnect();
      window.clearTimeout(timer);
      if (frame !== undefined) cancelAnimationFrame(frame);
      frame = undefined;
    };
    const restore = () => {
      if (stopped) return;
      window.scrollTo({ left: target.x, top: target.y, behavior: "instant" as ScrollBehavior });
      if (Math.abs(window.scrollY - target.y) <= 1) stop();
    };
    // Back can precede an asynchronous page becoming tall enough to restore.
    // Scroll writes can resize the page again: keep them out of the observer's
    // delivery cycle, and cancel queued work when navigation or input takes over.
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        if (stopped || frame !== undefined) return;
        frame = requestAnimationFrame(() => {
          frame = undefined;
          restore();
        });
      });
      observer.observe(document.body);
    }
    timer = window.setTimeout(stop, 5000);
    restore();
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    return () => { stop(); window.removeEventListener("wheel", stop); window.removeEventListener("touchstart", stop); };
  }, [entryKey, pathname, historyNavigation, delegated]);
}
